(ns client-portal.client.system-configuration.fields.views
  (:require [client-portal.client.system-configuration.data :as sc.d]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(defn field-label [field-name]
  (some-> (get {:rto-enabled "RTO Enabled"
                :enable-llm-rewriter "Enable LLM Rewriter"
                :ui-present "UI Present"
                :openai-semantic-vad "OpenAI Semantic VAD"
                :openai-semantic-vad-eagerness "OpenAI Semantic VAD Eagerness"
                :backup-vad "Backup VAD"}
               field-name
               (util/->capitalize-words field-name))
          (s/replace #"\bCes\b" "CES")))

;; Label | value | action on wide screens; the value drops below the label on narrow ones
(style/add-css (str ".sc-field-row { display: grid; grid-template-columns: minmax(0, 1fr) auto;"
                    " grid-template-areas: 'label action' 'value value'; column-gap: 16px; row-gap: 10px; }"
                    " .sc-field-row.sc-no-action { grid-template-columns: minmax(0, 1fr); grid-template-areas: 'label' 'value'; }"
                    " @media (min-width: 768px) {"
                    " .sc-field-row { grid-template-columns: minmax(0, 5fr) minmax(0, 7fr) 112px;"
                    " grid-template-areas: 'label value action'; column-gap: 24px; }"
                    " .sc-field-row.sc-no-action { grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);"
                    " grid-template-areas: 'label value'; } }"
                    " @media (prefers-reduced-motion: no-preference) { .sc-group-chevron { transition: transform 150ms ease-out; } }"))

(defn label [{:keys [text description font-size]}]
  [:div {:style {:display :flex
                 :flex-direction :column
                 :gap 4}}
   [:span {:style {:color (style/alc-color "Text/primary_header")
                   :font-size (or font-size 14)
                   :font-weight 500
                   :line-height 1.43}}
    text]
   (when (not-empty description)
     [:span {:style {:color (style/alc-color "Text/secondary")
                     :font-size 13
                     :line-height 1.45
                     :max-width "60ch"}}
      description])])

(defn empty-entries []
  [:span {:style {:color (style/alc-color "Text/secondary")
                  :font-size 13}}
   "No entries yet"])

(defn icon-action [{:keys [icon-type tooltip-content disabled? on-click]}]
  [alc/up-ghost-button {:icon-type icon-type
                        :icon "sm"
                        :tooltip-content tooltip-content
                        :disabled disabled?
                        :on-click on-click}])

(defn override-action [field]
  (if (= :inherit @(re/subscribe [:system-configuration/override-state- field]))
    [alc/up-ghost-button {:icon-type :EditIcon
                          :style {:height 32}
                          :on-click #(re/dispatch [:system-configuration/overwrite-field field])}
     "Override"]
    [alc/up-ghost-button {:icon-type :UndoIcon
                          :style {:height 32}
                          :on-click #(re/dispatch [:system-configuration/inherit-field field])}
     "Inherit"]))

(defn- count-text [n singular plural]
  (str n " " (if (= n 1) singular plural)))

(defn readout
  "An inherited value, shown as text rather than as a disabled input"
  [{:keys [field-type component-type] :as field} v]
  [:span {:style {:color (style/alc-color "Text/secondary")
                  :display "-webkit-box"
                  :font-size 14
                  :line-height 1.43
                  :overflow :hidden
                  :overflow-wrap :anywhere
                  :padding-top 7
                  :white-space :pre-wrap
                  "WebkitBoxOrient" "vertical"
                  "WebkitLineClamp" 3}}
   (cond
     (nil? v) "Not set"
     (= component-type :dashboard-fields) (count-text (count v) "row" "rows")
     (= field-type :boolean) (if v "On" "Off")
     (sc.d/object-vector-field? field) (count-text (count v) "entry" "entries")
     (and (coll? v) (empty? v)) "None"
     (coll? v) (s/join ", " (map str v))
     :else (str v))])

(defn- invalid-message [{:keys [field-type min max]}]
  (cond
    (and (contains? #{:int :double :float} field-type) (some? min) (some? max)) (str "Enter a number from " min " to " max)
    (and (contains? #{:int :double :float} field-type) (some? min)) (str "Enter a number of at least " min)
    (and (contains? #{:int :double :float} field-type) (some? max)) (str "Enter a number up to " max)
    (= field-type :int) "Enter a whole number"
    (contains? #{:double :float} field-type) "Enter a number"
    :else "This setting needs a value"))

(defn object-vector-editor [{:keys [field value disabled? on-change]}]
  (let [inner-fields (-> field :vector-type :map-values)
        rows (sc.d/vec-of-maps->form-rows (when (sequential? value) (vec value)))
        update-row (fn [row-id k v]
                     (on-change (mapv #(if (= (:id %) row-id) (assoc % k v) %) rows)))]
    [:div {:style {:display :flex
                   :flex-direction :column
                   :gap 8}}
     (if (empty? rows)
       [empty-entries]
       (into [:div {:style {:display :flex
                            :flex-direction :column
                            :gap 6}}
              [:div {:style {:display :flex
                             :gap 8
                             :padding-right 36}}
               (for [{:keys [field-name]} inner-fields]
                 ^{:key field-name}
                 [:span {:style {:flex 1
                                 :color (style/alc-color "Text/secondary")
                                 :font-size 12}}
                  (field-label field-name)])]]
             (for [row rows]
               ^{:key (:id row)}
               [:div {:style {:display :flex
                              :align-items :center
                              :gap 8}}
                (for [{:keys [field-name]} inner-fields]
                  ^{:key field-name}
                  [:div {:style {:flex 1
                                 :min-width 0}}
                   [alc/up-input {:default-value (or (get row field-name) "")
                                  :placeholder (field-label field-name)
                                  :disabled disabled?
                                  :on-change #(update-row (:id row) field-name (.. % -target -value))}]])
                [icon-action {:icon-type :DeleteIcon
                              :tooltip-content "Remove"
                              :disabled? disabled?
                              :on-click #(on-change (filterv (fn [r] (not= (:id r) (:id row))) rows))}]])))
     [alc/up-button {:icon-type :AddIcon
                     :disabled disabled?
                     :style {:align-self :flex-start}
                     :on-click #(on-change (conj rows (-> (zipmap (map :field-name inner-fields) (repeat ""))
                                                          (assoc :id (util/random-uuid-string)))))}
      "Add Entry"]]))

(defn string-list-editor [{:keys [value disabled? on-change]}]
  (let [rows (sc.d/strings->form-rows (when (sequential? value) (vec value)))]
    [:div {:style {:display :flex
                   :flex-direction :column
                   :gap 8}}
     (if (empty? rows)
       [empty-entries]
       (into [:div {:style {:display :flex
                            :flex-direction :column
                            :gap 6}}]
             (for [row rows]
               ^{:key (:id row)}
               [:div {:style {:display :flex
                              :align-items :center
                              :gap 8}}
                [:div {:style {:flex 1
                               :min-width 0}}
                 [alc/up-input {:default-value (:value row)
                                :placeholder "Phrase"
                                :disabled disabled?
                                :on-change #(on-change (mapv (fn [r]
                                                               (cond-> r
                                                                       (= (:id r) (:id row)) (assoc :value (.. % -target -value))))
                                                             rows))}]]
                [icon-action {:icon-type :DeleteIcon
                              :tooltip-content "Remove"
                              :disabled? disabled?
                              :on-click #(on-change (filterv (fn [r] (not= (:id r) (:id row))) rows))}]])))
     [alc/up-button {:icon-type :AddIcon
                     :disabled disabled?
                     :style {:align-self :flex-start}
                     :on-click #(on-change (conj rows {:id (util/random-uuid-string)
                                                       :value ""}))}
      "Add Entry"]]))

(defn dashboard-fields-editor [{:keys [value disabled? on-change]}]
  (let [component-options (->> ["value-block" "message-block" "metric-status-indicator" "vi-marketing-score"
                                "vi-efficiency-score" "vi-satisfaction-score" "vi-marketing-gauge" "vi-efficiency-gauge"
                                "vi-satisfaction-gauge" "header" "spacer" "percent-of-conversations"
                                "percent-of-conversations-gauge" "count-of-conversations" "vi-out-of-stock-message"
                                "vi-percent-of-conversations-ranking" "vi-last-n-conversations-metrics"
                                "vi-child-value-gauge" "vi-most-common-tag-instructions"]
                               (mapv (fn [v]
                                       {:text (-> v (util/->capitalize-words) (s/replace #"\bVi\b" "VI"))
                                        :value v}))
                               (sort-by :text))
        new-column (fn []
                     {:id (util/random-uuid-string)
                      :props "{}"})
        rows (or (not-empty value) [[(new-column)]])
        swap-items (fn [items i j]
                     (assoc items i (get items j) j (get items i)))
        drop-item (fn [items i]
                    (into (subvec items 0 i) (subvec items (inc i))))
        update-row (fn [row-idx f]
                     (on-change (update rows row-idx f)))]
    [:div {:style {:display :flex
                   :flex-direction :column
                   :gap 12}}
     (for [[row-idx row] (map-indexed vector rows)]
       ^{:key row-idx}
       [:div {:style {:background-color (style/alc-color "Background/secondary")
                      :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                      :border-radius 8
                      :display :flex
                      :flex-direction :column
                      :gap 12
                      :padding 12}}
        [:div {:style {:display :flex
                       :align-items :center
                       :gap 4}}
         [:span {:style {:flex 1
                         :color (style/alc-color "Text/primary_header")
                         :font-size 14
                         :font-weight 500}}
          (str "Row " (inc row-idx))]
         [icon-action {:icon-type :ArrowUpIcon
                       :tooltip-content "Move Row Up"
                       :disabled? (or disabled? (zero? row-idx))
                       :on-click #(on-change (swap-items rows row-idx (dec row-idx)))}]
         [icon-action {:icon-type :ArrowDownIcon
                       :tooltip-content "Move Row Down"
                       :disabled? (or disabled? (= row-idx (dec (count rows))))
                       :on-click #(on-change (swap-items rows row-idx (inc row-idx)))}]
         [icon-action {:icon-type :DeleteIcon
                       :tooltip-content "Remove Row"
                       :disabled? (or disabled? (= (count rows) 1))
                       :on-click #(on-change (drop-item rows row-idx))}]]
        (for [[col-idx col] (map-indexed vector row)]
          ^{:key (or (:id col) col-idx)}
          [:div {:style (cond-> {:background-color (style/alc-color "Background/card_hex")
                                 :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                                 :border-radius 8
                                 :display :flex
                                 :flex-direction :column
                                 :gap 10
                                 :padding 12}
                                (:hide col) (assoc :opacity 0.6))}
           [:div {:style {:display :flex
                          :align-items :center
                          :gap 4}}
            [:span {:style {:flex 1
                            :color (style/alc-color "Text/primary_header")
                            :font-size 14
                            :font-weight 500}}
             (str "Column " (inc col-idx))]
            [icon-action {:icon-type :ArrowUpIcon
                          :tooltip-content "Move Column Up"
                          :disabled? (or disabled? (zero? col-idx))
                          :on-click #(update-row row-idx (fn [r] (swap-items r col-idx (dec col-idx))))}]
            [icon-action {:icon-type :ArrowDownIcon
                          :tooltip-content "Move Column Down"
                          :disabled? (or disabled? (= col-idx (dec (count row))))
                          :on-click #(update-row row-idx (fn [r] (swap-items r col-idx (inc col-idx))))}]
            [icon-action {:icon-type :DeleteIcon
                          :tooltip-content "Remove Column"
                          :disabled? disabled?
                          :on-click #(update-row row-idx (fn [r] (drop-item r col-idx)))}]]
           [:div {:style {:display :flex
                          :flex-direction :column
                          :gap 10}}
            [:div {:style {:display :flex
                           :flex-direction :column
                           :gap 6}}
             [label {:text "Component"}]
             [alc/up-dropdown {:value (:component col)
                               :placeholder "Select component"
                               :disabled disabled?
                               :options component-options
                               :on-change #(update-row row-idx (fn [r] (assoc-in r [col-idx :component] %)))}]]
            [:div {:style {:display :flex
                           :flex-direction :column
                           :gap 6}}
             [label {:text "Props"}]
             [alc/up-textarea {:default-value (:props col)
                               :placeholder "Component props (JSON)"
                               :disabled disabled?
                               :wrap "off"
                               :style {:font-family "monospace"
                                       :min-height 90
                                       :overflow-x :auto}
                               :on-change #(update-row row-idx (fn [r] (assoc-in r [col-idx :props] (.. % -target -value))))}]]
            [:div {:style {:display :flex
                           :align-items :flex-end
                           :gap 16}}
             [:div {:style {:display :flex
                            :flex-direction :column
                            :gap 6
                            :width 200}}
              [label {:text "Refresh Seconds"}]
              [alc/up-input {:default-value (or (:refresh-seconds col) "")
                             :type "number"
                             :placeholder "Refresh seconds"
                             :disabled disabled?
                             :on-change #(let [v (util/parse-int (.. % -target -value))]
                                           (update-row row-idx (fn [r]
                                                                 (if v
                                                                   (assoc-in r [col-idx :refresh-seconds] v)
                                                                   (update r col-idx dissoc :refresh-seconds)))))}]]
             [:label {:style {:display :flex
                              :align-items :center
                              :gap 8
                              :height 36
                              :cursor (if disabled? :default :pointer)}}
              [alc/up-checkbox {:checked (boolean (:hide col))
                                :disabled disabled?
                                :on-checked-change #(update-row row-idx (fn [r] (assoc-in r [col-idx :hide] (boolean %))))}]
              [:span {:style {:color (style/alc-color "Text/primary")
                              :font-size 14}}
               "Hide"]]]]])
        [alc/up-ghost-button {:icon-type :AddIcon
                              :disabled disabled?
                              :style {:align-self :flex-start}
                              :on-click #(update-row row-idx (fn [r] (conj r (new-column))))}
         "Add Column"]])
     [alc/up-button {:icon-type :AddIcon
                     :disabled disabled?
                     :style {:align-self :flex-start}
                     :on-click #(on-change (conj rows [(new-column)]))}
      "Add Row"]]))

(defn unsupported-field [{:keys [field-name field-type component-type]}]
  [:span {:style {:color (style/alc-color "Common/error")
                  :font-size 13}}
   (str "Unsupported field " (name field-name) " of type " (name (or component-type field-type)))])

(defn field-editor [{:keys [field-type component-type enum-values set-type vector-type optional props min max]
                     :or {props {}}
                     :as field}
                    {:keys [value invalid?]}]
  (let [on-change #(re/dispatch [:system-configuration/change-field-value field %])
        placeholder (field-label (:field-name field))
        options-for (fn [values]
                      (mapv (fn [v] {:text v :value v}) values))
        number-input (fn [parse]
                       [alc/up-input (util/deep-merge (cond-> {:default-value (if (nil? value) "" value)
                                                               :type "number"
                                                               :step (if (= field-type :int) 1 "any")
                                                               :placeholder placeholder
                                                               :aria-invalid invalid?
                                                               :on-change #(on-change (parse (.. % -target -value)))}
                                                              (some? min) (assoc :min min)
                                                              (some? max) (assoc :max max))
                                                      props)])]
    (case (or component-type field-type)
      :dashboard-fields [dashboard-fields-editor {:value value
                                                  :on-change on-change}]

      :boolean [:div {:style {:display :flex
                              :align-items :center
                              :min-height 36}}
                [alc/up-toggle {:checked (boolean value)
                                :aria-label placeholder
                                :on-checked-change on-change}]]

      :enum [:div {:style {:display :flex
                           :align-items :center
                           :gap 4}}
             [:div {:style {:flex 1
                            :min-width 0}}
              [alc/up-dropdown (util/deep-merge {:value value
                                                 :placeholder placeholder
                                                 :aria-invalid invalid?
                                                 :options (options-for enum-values)
                                                 :on-change on-change}
                                                props)]]
             (when (and optional (some? value))
               [icon-action {:icon-type :CrossIcon
                             :tooltip-content "Clear"
                             :on-click #(on-change nil)}])]

      :int (number-input util/parse-int)

      (:double :float) (number-input util/parse-float)

      :textarea [alc/up-textarea (util/deep-merge {:default-value (or value "")
                                                   :placeholder placeholder
                                                   :aria-invalid invalid?
                                                   :style {:min-height 90}
                                                   :on-change #(on-change (.. % -target -value))}
                                                  props)]

      :string [alc/up-input (util/deep-merge {:default-value (or value "")
                                              :placeholder placeholder
                                              :aria-invalid invalid?
                                              :on-change #(on-change (.. % -target -value))}
                                             props)]

      :set (if (= (:field-type set-type) :enum)
             [alc/up-multiple-dropdown (util/deep-merge {:value (vec value)
                                                         :placeholder placeholder
                                                         :options (options-for (:enum-values set-type))
                                                         :on-change #(on-change (set %))}
                                                        props)]
             [unsupported-field field])

      :vector (cond
                (= (:field-type vector-type) :enum)
                [alc/up-multiple-dropdown (util/deep-merge {:value (vec value)
                                                            :placeholder placeholder
                                                            :options (options-for (:enum-values vector-type))
                                                            :on-change on-change}
                                                           props)]

                (sc.d/object-vector-field? field)
                [object-vector-editor {:field field
                                       :value value
                                       :on-change on-change}]

                (sc.d/string-list-field? field)
                [string-list-editor {:value value
                                     :on-change on-change}]

                :else
                [unsupported-field field])

      [unsupported-field field])))

(def ^:private group-indent 28)

(def ^:private group-guide-offset
  "Where a group's guide line sits, left of its children (under the chevron)"
  (- group-indent 10))

(defn- gutter
  "How far an override reaches left of its row: into the page gutter at the top level, and onto its group's guide line
   when nested, so the mark replaces that stretch of the guide rather than sitting beside it"
  [depth]
  (if (pos? depth)
    (inc group-guide-offset)
    16))

(defn- override-band
  "Tints anything set at this level. The shadows widen the tint into the gutter and past the action without moving
   the row's content."
  [depth]
  (let [tint (util/hex->rgba (style/alc-color "Common/active") 0.08)]
    {:background-color tint
     :box-shadow (str "-" (gutter depth) "px 0 0 " tint ", 8px 0 0 " tint)}))

(defn- override-mark [depth]
  [:span {:aria-hidden true
          :style {:background-color (style/alc-color "Common/active")
                  :bottom 0
                  :left (- (gutter depth))
                  :position :absolute
                  :top -1
                  :width 2}}])

(defn- row-style []
  {:border-top (str "1px solid " (style/alc-color "Border/divider_secondary"))
   :padding "16px 0"
   :position :relative})

(defn- group-summary [field]
  (let [child-level? (not= :concept @(re/subscribe [:system-configuration/level-]))
        {:keys [overridden total invalid]} @(re/subscribe [:system-configuration/group-summary- field])]
    [:span {:style {:align-items :center
                    :color (style/alc-color (if (pos? invalid) "Common/error" "Text/secondary"))
                    :display :flex
                    :flex-shrink 0
                    :font-size 13
                    :min-height 24}}
     (cond
       (pos? invalid) (str invalid (if (= invalid 1) " setting needs" " settings need") " fixing")
       (not child-level?) (count-text total "setting" "settings")
       (zero? overridden) (str (count-text total "setting" "settings") ", all inherited")
       :else (str (count-text total "setting" "settings") ", " overridden " overridden here"))]))

(declare field-row)

(defn- group-row [{:keys [field-name description map-values nesting-idx] :as field}]
  (let [child-level? (not= :concept @(re/subscribe [:system-configuration/level-]))
        turned-off? (and child-level? @(re/subscribe [:system-configuration/map-field-disabled?- field]))
        expanded? (and (not turned-off?) @(re/subscribe [:system-configuration/group-expanded?- field]))]
    [:div
     [:div {:style (merge (row-style)
                          {:display :flex
                           :align-items :flex-start
                           :justify-content :space-between
                           :gap 16}
                          (when turned-off?
                            (override-band nesting-idx)))}
      (when turned-off?
        [override-mark nesting-idx])
      [:button {:type "button"
                :aria-expanded expanded?
                :disabled turned-off?
                :class-name (str "rounded-md text-left outline-none transition-colors focus-visible:ring-[3px] "
                                 "focus-visible:ring-ring/50" (when-not turned-off? " hover:bg-up-background-hover"))
                :style {:background :none
                        :color :inherit
                        :cursor (if turned-off? :default :pointer)
                        :display :flex
                        :flex "1 1 auto"
                        :flex-direction :column
                        :gap 4
                        :margin "-4px 0 -4px -4px"
                        :min-width 0
                        :padding 4}
                :on-click #(re/dispatch [:system-configuration/toggle-group field])}
       [:span {:style {:align-items :center
                       :display :flex
                       :gap 8
                       :min-height 24}}
        [:span {:class-name "sc-group-chevron"
                :style {:display :flex
                        :transform (when expanded? "rotate(90deg)")
                        :visibility (when turned-off? :hidden)}}
         [alc/up-secondary-icon {:icon-type :ChevronRightIcon}]]
        [:span {:style {:color (style/alc-color "Text/primary_header")
                        :font-size 16
                        :font-weight 500
                        :line-height 1.25}}
         (field-label field-name)]]
       (when-let [description (if turned-off?
                                "Turned off here, so nothing in this group is inherited."
                                (not-empty description))]
         ;; Indented past the chevron so it lines up under the title
         [:span {:style {:color (style/alc-color "Text/secondary")
                         :font-size 13
                         :line-height 1.45
                         :max-width "60ch"
                         :padding-left 28}}
          description])]
      (if turned-off?
        [alc/up-ghost-button {:icon-type :UndoIcon
                              :style {:height 32
                                      :margin-top -4}
                              :on-click #(re/dispatch [:system-configuration/set-map-field-disabled field false])}
         "Turn Back On"]
        [group-summary field])]
     (when expanded?
       (into [:div {:style {:margin-left group-indent
                            :position :relative}}
              [:span {:aria-hidden true
                      :style {:background-color (style/alc-color "Border/secondary_inp")
                              :bottom 0
                              :left (- group-guide-offset)
                              :position :absolute
                              :top 0
                              :width 1}}]]
             (for [f map-values]
               ^{:key (:field-name f)}
               [field-row f])))]))

(defn field-row [{:keys [field-type field-name description nesting-idx] :as field}]
  (let [child-level? (not= :concept @(re/subscribe [:system-configuration/level-]))]
    (if (= field-type :map)
      [group-row field]
      (let [override-state @(re/subscribe [:system-configuration/override-state- field])
            value @(re/subscribe [:system-configuration/field-value- field])
            invalid? (not @(re/subscribe [:system-configuration/field-valid?- field]))
            inherit? (and child-level? (= override-state :inherit))
            overridden? (and child-level? (not inherit?))]
        [:div {:class-name (str "sc-field-row" (when-not child-level? " sc-no-action"))
               :style (merge (row-style)
                             {:align-items :start}
                             (when overridden?
                               (override-band nesting-idx)))}
         (when overridden?
           [override-mark nesting-idx])
         [:div {:style {:grid-area "label"
                        :padding-top 8}}
          [label {:text (field-label field-name)
                  :description description}]]
         ;; Inputs are uncontrolled, so they remount to pick up the value when switching between inherit and override
         ^{:key (name override-state)}
         [:div {:style {:grid-area "value"
                        :display :flex
                        :flex-direction :column
                        :gap 6}}
          (if inherit?
            [readout field value]
            [field-editor field {:value value
                                 :invalid? invalid?}])
          (when invalid?
            [:span {:style {:color (style/alc-color "Common/error")
                            :font-size 12
                            :line-height 1.33}}
             (invalid-message field)])]
         (when child-level?
           [:div {:style {:grid-area "action"
                          :justify-self :end
                          :padding-top 2}}
            [override-action field]])]))))
